import { useState, useEffect, useMemo } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import BarcodeScanner from "@/components/BarcodeScanner";
import { 
  Plus, 
  Minus, 
  Search, 
  Package,
  AlertTriangle,
  Trash2,
  Edit,
  Copy,
  Palette,
  Sparkles,
  Droplets,
  ShoppingBag,
  Beaker,
  Filter,
  BarChart3,
  TrendingDown,
  Box,
  Tag,
  Camera,
  ArrowDownToLine,
  ArrowUpFromLine,
  Truck
} from "lucide-react";

// Categorie per prodotti TECNICI (uso interno salone)
const CATEGORIE_TECNICHE = [
  { value: "colore", label: "Colore", icon: Palette, color: "bg-purple-100 text-purple-700" },
  { value: "decolorante", label: "Decolorante", icon: Sparkles, color: "bg-amber-100 text-amber-700" },
  { value: "trattamento", label: "Trattamento", icon: Beaker, color: "bg-emerald-100 text-emerald-700" },
  { value: "ossigeno", label: "Ossigeno", icon: Droplets, color: "bg-blue-100 text-blue-700" },
  { value: "monouso", label: "Monouso", icon: Package, color: "bg-gray-100 text-gray-700" },
  { value: "accessorio_tecnico", label: "Accessori Tecnici", icon: Box, color: "bg-slate-100 text-slate-700" },
];

// Categorie per prodotti RIVENDITA (vendita al cliente)
const CATEGORIE_RIVENDITA = [
  { value: "shampoo", label: "Shampoo", icon: Droplets, color: "bg-cyan-100 text-cyan-700" },
  { value: "balsamo", label: "Balsamo", icon: Droplets, color: "bg-teal-100 text-teal-700" },
  { value: "maschera", label: "Maschera", icon: Beaker, color: "bg-pink-100 text-pink-700" },
  { value: "styling", label: "Styling", icon: Sparkles, color: "bg-orange-100 text-orange-700" },
  { value: "finish", label: "Finish", icon: Sparkles, color: "bg-rose-100 text-rose-700" },
  { value: "accessorio", label: "Accessori", icon: ShoppingBag, color: "bg-indigo-100 text-indigo-700" },
];

// Sottocategorie per Monouso
const SOTTOCATEGORIE_MONOUSO = [
  { value: "guanti", label: "Guanti" },
  { value: "mantelle", label: "Mantelle" },
  { value: "carta_alluminio", label: "Carta Alluminio" },
  { value: "cuffie", label: "Cuffie" },
  { value: "asciugamani", label: "Asciugamani" },
  { value: "pennelli", label: "Pennelli/Spazzole" },
  { value: "altro_monouso", label: "Altro" },
];

// Unità di misura
const UNITA_MISURA = [
  { value: "ml", label: "ml (millilitri)" },
  { value: "gr", label: "gr (grammi)" },
  { value: "pz", label: "pz (pezzi)" },
  { value: "conf", label: "conf (confezione)" },
  { value: "rotolo", label: "rotolo" },
];

// Sottocategorie per Colore
const SOTTOCATEGORIE_COLORE = [
  { value: "permanente", label: "Permanente" },
  { value: "semipermanente", label: "Semipermanente" },
  { value: "tonalizzante", label: "Tonalizzante" },
  { value: "gloss", label: "Gloss" },
];

// Toni base disponibili (possono essere personalizzati per linea)
const TONI_DEFAULT = [
  { value: "1", label: "1 - Nero", hex: "#0a0908" },
  { value: "2", label: "2 - Bruno Scurissimo", hex: "#1c1108" },
  { value: "3", label: "3 - Bruno Scuro", hex: "#3b2219" },
  { value: "4", label: "4 - Bruno", hex: "#4e3524" },
  { value: "5", label: "5 - Bruno Chiaro", hex: "#6a4730" },
  { value: "6", label: "6 - Biondo Scuro", hex: "#8b6914" },
  { value: "7", label: "7 - Biondo", hex: "#b8860b" },
  { value: "8", label: "8 - Biondo Chiaro", hex: "#d4a857" },
  { value: "9", label: "9 - Biondo Chiarissimo", hex: "#e8c872" },
  { value: "10", label: "10 - Biondo Platino", hex: "#f7e7a2" }
];

// Riflessi base disponibili (possono essere personalizzati per linea)
const RIFLESSI_DEFAULT = [
  { value: "0", label: ".0 - Naturale", hex: "#8b7355" },
  { value: "1", label: ".1 - Cenere", hex: "#a9a9a9" },
  { value: "2", label: ".2 - Irisé/Viola", hex: "#9370db" },
  { value: "3", label: ".3 - Dorato", hex: "#ffd700" },
  { value: "4", label: ".4 - Rame", hex: "#cd7f32" },
  { value: "5", label: ".5 - Mogano", hex: "#800020" },
  { value: "6", label: ".6 - Rosso", hex: "#dc143c" },
  { value: "7", label: ".7 - Mat/Verde", hex: "#556b2f" },
  { value: "8", label: ".8 - Perla/Beige", hex: "#f5deb3" },
  { value: "9", label: ".9 - Soft/Miele", hex: "#daa520" }
];

// Sottocategorie per Decolorante
const SOTTOCATEGORIE_DECOLORANTE = [
  { value: "polvere", label: "Polvere Decolorante" },
  { value: "crema", label: "Crema Decolorante" },
  { value: "olio", label: "Olio Decolorante" },
];

// Sottocategorie per Trattamento
const SOTTOCATEGORIE_TRATTAMENTO = [
  { value: "ristrutturante", label: "Ristrutturante" },
  { value: "cheratina", label: "Cheratina" },
  { value: "idratante", label: "Idratante" },
  { value: "anticaduta", label: "Anticaduta" },
  { value: "protettivo", label: "Protettivo Termico" },
];

export default function Magazzino() {
  const { token } = useAuth();
  const [prodotti, setProdotti] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("tecnico");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategoria, setFilterCategoria] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProdotto, setEditingProdotto] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [filterSottocategoria, setFilterSottocategoria] = useState("all"); // Nuovo filtro sottocategoria
  // Soglia sottoscorta globale (caricata da /api/configurazione)
  const [sogliaGlobale, setSogliaGlobale] = useState(5);
  
  // Stato per Carico/Scarico
  const [caricoScaricoOpen, setCaricoScaricoOpen] = useState(false);
  const [caricoScaricoMode, setCaricoScaricoMode] = useState("carico"); // "carico" o "scarico"
  const [selectedProdotto, setSelectedProdotto] = useState(null);
  const [quantitaMovimento, setQuantitaMovimento] = useState(1);
  const [scannerCaricoOpen, setScannerCaricoOpen] = useState(false);
  
  // Stato per Export CSV
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [exportSoloRivendita, setExportSoloRivendita] = useState(false);
  
  const [formData, setFormData] = useState({
    nome: "",
    marca: "",
    tipo_prodotto: "tecnico",
    categoria: "colore",
    sottocategoria: "",
    volume_ml: "",
    unita_misura: "ml", // ml, gr, pz, conf, rotolo
    codice_prodotto: "",
    codice_barre: "",
    fornitore: "",
    quantita: 0,
    prezzo_acquisto: 0,
    prezzo_vendita: 0,
    soglia_minima: 3,
    note: "",
    genere_target: "unisex",  // per analisi rivendita per genere
    // Campi per linee colore
    toni_disponibili: [],
    riflessi_disponibili: []
  });

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportMenuOpen && !event.target.closest('[data-testid="export-csv-btn"]') && !event.target.closest('.export-menu')) {
        setExportMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [exportMenuOpen]);

  // Initial data fetch
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchProdotti();
    fetchFornitori();
    fetchSogliaGlobale();
  }, []);

  const fetchSogliaGlobale = async () => {
    try {
      const res = await axios.get(`${API}/configurazione`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.soglia_sottoscorta_globale != null) {
        setSogliaGlobale(res.data.soglia_sottoscorta_globale);
      }
    } catch {
      // usa default 5
    }
  };

  const fetchProdotti = async () => {
    try {
      const response = await axios.get(`${API}/prodotti`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProdotti(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento prodotti");
    } finally {
      setLoading(false);
    }
  };

  const fetchFornitori = async () => {
    try {
      const response = await axios.get(`${API}/fornitori`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFornitori(response.data);
    } catch (error) {
      console.error('Errore caricamento fornitori:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = {
        ...formData,
        volume_ml: formData.volume_ml ? parseInt(formData.volume_ml) : null,
        quantita: parseInt(formData.quantita),
        prezzo_acquisto: parseFloat(formData.prezzo_acquisto),
        prezzo_vendita: parseFloat(formData.prezzo_vendita),
        soglia_minima: parseInt(formData.soglia_minima)
      };

      if (editingProdotto) {
        await axios.put(`${API}/prodotti/${editingProdotto.id}`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Prodotto aggiornato");
      } else {
        await axios.post(`${API}/prodotti`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Prodotto aggiunto");
      }
      setDialogOpen(false);
      resetForm();
      setTimeout(() => fetchProdotti(), 150);
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleQuantitaChange = async (id, delta) => {
    try {
      await axios.patch(`${API}/prodotti/${id}/quantita?delta=${delta}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProdotti(prodotti.map(p => 
        p.id === id ? { ...p, quantita: Math.max(0, p.quantita + delta) } : p
      ));
    } catch (error) {
      toast.error("Errore nell'aggiornamento quantità");
    }
  };

  // Funzione Carico/Scarico
  const handleCaricoScarico = async () => {
    if (!selectedProdotto || quantitaMovimento <= 0) {
      toast.error("Seleziona un prodotto e inserisci una quantità valida");
      return;
    }
    
    const delta = caricoScaricoMode === "carico" ? quantitaMovimento : -quantitaMovimento;
    
    try {
      await axios.patch(`${API}/prodotti/${selectedProdotto.id}/quantita?delta=${delta}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const nuovaQta = Math.max(0, selectedProdotto.quantita + delta);
      setProdotti(prodotti.map(p => 
        p.id === selectedProdotto.id ? { ...p, quantita: nuovaQta } : p
      ));
      
      toast.success(
        caricoScaricoMode === "carico" 
          ? `✅ Caricati ${quantitaMovimento} pz di ${selectedProdotto.nome}` 
          : `📤 Scaricati ${quantitaMovimento} pz di ${selectedProdotto.nome}`
      );
      
      // Reset
      setSelectedProdotto(null);
      setQuantitaMovimento(1);
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo prodotto?")) return;
    try {
      await axios.delete(`${API}/prodotti/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Prodotto eliminato");
      setTimeout(() => fetchProdotti(), 100);
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  // Export CSV per WooCommerce/Shopify
  const handleExportCSV = async (formato) => {
    try {
      setExportMenuOpen(false);
      toast.loading("Preparazione export...");
      
      const response = await axios.get(
        `${API}/prodotti/export-csv?formato=${formato}&solo_rivendita=${exportSoloRivendita}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Crea il download
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `prodotti_${formato}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.dismiss();
      toast.success(`Export ${formato.toUpperCase()} completato!`);
    } catch (error) {
      toast.dismiss();
      console.error('Export error:', error);
      toast.error("Errore durante l'export");
    }
  };

  const openEditDialog = (prodotto) => {
    setEditingProdotto(prodotto);
    setFormData({
      nome: prodotto.nome || "",
      marca: prodotto.marca || "",
      tipo_prodotto: prodotto.tipo_prodotto || "tecnico",
      categoria: prodotto.categoria || "colore",
      sottocategoria: prodotto.sottocategoria || "",
      volume_ml: prodotto.volume_ml || "",
      unita_misura: prodotto.unita_misura || "ml",
      codice_prodotto: prodotto.codice_prodotto || "",
      fornitore: prodotto.fornitore || "",
      quantita: prodotto.quantita || 0,
      prezzo_acquisto: prodotto.prezzo_acquisto || 0,
      prezzo_vendita: prodotto.prezzo_vendita || 0,
      soglia_minima: prodotto.soglia_minima || 3,
      note: prodotto.note || "",
      genere_target: prodotto.genere_target || "unisex",
      toni_disponibili: prodotto.toni_disponibili || [],
      riflessi_disponibili: prodotto.riflessi_disponibili || []
    });
    setDialogOpen(true);
  };

  // Duplica un prodotto esistente: pre-compila il form con le caratteristiche
  // del prodotto sorgente (marca, categoria, sottocategoria, fornitore, prezzi, toni, riflessi),
  // azzera ciò che deve essere univoco/aggiornato (codice barcode, quantità) e modifica il nome
  // aggiungendo "(copia)" come suggerimento. Salvando si crea un NUOVO prodotto.
  const openDuplicateDialog = (prodotto) => {
    setEditingProdotto(null); // NON è editing → salvataggio creerà nuovo prodotto
    setFormData({
      nome: prodotto.nome ? `${prodotto.nome} (copia)` : "",
      marca: prodotto.marca || "",
      tipo_prodotto: prodotto.tipo_prodotto || "tecnico",
      categoria: prodotto.categoria || "colore",
      sottocategoria: prodotto.sottocategoria || "",
      volume_ml: prodotto.volume_ml || "",
      unita_misura: prodotto.unita_misura || "ml",
      codice_prodotto: "", // barcode univoco → svuotato
      fornitore: prodotto.fornitore || "",
      quantita: 0, // nuova giacenza parte da 0
      prezzo_acquisto: prodotto.prezzo_acquisto || 0,
      prezzo_vendita: prodotto.prezzo_vendita || 0,
      soglia_minima: prodotto.soglia_minima || 3,
      note: prodotto.note || "",
      genere_target: prodotto.genere_target || "unisex",
      toni_disponibili: prodotto.toni_disponibili || [],
      riflessi_disponibili: prodotto.riflessi_disponibili || []
    });
    setDialogOpen(true);
    toast.info(`Duplicato da: ${prodotto.nome}. Modifica nome/tono e salva.`);
  };

  const openNewDialog = (tipo) => {
    resetForm();
    setFormData(prev => ({ 
      ...prev, 
      tipo_prodotto: tipo,
      categoria: tipo === "tecnico" ? "colore" : "shampoo",
      soglia_minima: sogliaGlobale
    }));
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingProdotto(null);
    setFormData({
      nome: "",
      marca: "",
      tipo_prodotto: "tecnico",
      categoria: "colore",
      sottocategoria: "",
      volume_ml: "",
      unita_misura: "ml",
      codice_prodotto: "",
      fornitore: "",
      quantita: 0,
      prezzo_acquisto: 0,
      prezzo_vendita: 0,
      soglia_minima: sogliaGlobale,
      note: "",
      genere_target: "unisex",
      toni_disponibili: [],
      riflessi_disponibili: []
    });
  };

  // Filtra prodotti per tab e filtri
  const filteredProdotti = useMemo(() => {
    return prodotti.filter(p => {
      // Filtra per tipo (tecnico/rivendita)
      const tipo = p.tipo_prodotto || "tecnico";
      if (tipo !== activeTab) return false;
      
      // Filtra per ricerca
      const matchSearch = searchTerm === "" || 
        p.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.marca?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.codice_prodotto?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Filtra per categoria
      const matchCategoria = filterCategoria === "all" || p.categoria === filterCategoria;
      
      // Filtra per sottocategoria
      const matchSottocategoria = filterSottocategoria === "all" || p.sottocategoria === filterSottocategoria;
      
      return matchSearch && matchCategoria && matchSottocategoria;
    });
  }, [prodotti, activeTab, searchTerm, filterCategoria, filterSottocategoria]);

  // Statistiche
  const stats = useMemo(() => {
    const tecnici = prodotti.filter(p => (p.tipo_prodotto || "tecnico") === "tecnico");
    const rivendita = prodotti.filter(p => p.tipo_prodotto === "rivendita");
    const scorteBasse = prodotti.filter(p => p.quantita <= p.soglia_minima);
    const valoreTotale = prodotti.reduce((acc, p) => acc + (p.quantita * p.prezzo_acquisto), 0);
    
    return {
      tecnici: tecnici.length,
      rivendita: rivendita.length,
      scorteBasse: scorteBasse.length,
      valoreTotale
    };
  }, [prodotti]);

  // Categorie attive in base al tab
  const categorieAttive = activeTab === "tecnico" ? CATEGORIE_TECNICHE : CATEGORIE_RIVENDITA;

  // Ottieni sottocategorie in base alla categoria selezionata
  const getSottocategorie = (categoria) => {
    switch(categoria) {
      case "colore": return SOTTOCATEGORIE_COLORE;
      case "decolorante": return SOTTOCATEGORIE_DECOLORANTE;
      case "trattamento": return SOTTOCATEGORIE_TRATTAMENTO;
      case "monouso": return SOTTOCATEGORIE_MONOUSO;
      default: return [];
    }
  };

  // Ottieni l'unità di misura di default in base alla categoria
  const getDefaultUnitaMisura = (categoria) => {
    switch(categoria) {
      case "monouso": return "pz";
      case "ossigeno": return "ml";
      case "colore": return "ml";
      default: return "ml";
    }
  };

  // Trova info categoria
  const getCategoriaInfo = (categoria) => {
    return [...CATEGORIE_TECNICHE, ...CATEGORIE_RIVENDITA].find(c => c.value === categoria) || 
      { label: categoria, color: "bg-slate-100 text-slate-700", icon: Package };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-6" data-testid="magazzino-page">
      {/* Header Compatto */}
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-lg sm:text-2xl font-bold text-slate-800 flex items-center gap-1 sm:gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
          <Package className="w-5 h-5 sm:w-7 sm:h-7 text-purple-600" />
          <span className="hidden sm:inline">Magazzino</span>
          <span className="sm:hidden">Mag.</span>
        </h1>
        
        <div className="flex gap-1 sm:gap-2">
          {/* Export CSV - compatto su mobile */}
          <div className="relative">
            <Button
              onClick={() => setExportMenuOpen(!exportMenuOpen)}
              variant="outline"
              size="sm"
              className="h-8 px-2 sm:px-3 text-xs sm:text-sm"
              data-testid="export-csv-btn"
            >
              <ArrowDownToLine className="w-3.5 h-3.5 sm:mr-1" />
              <span className="hidden sm:inline">Export</span>
            </Button>
            {exportMenuOpen && (
              <div className="export-menu absolute right-0 mt-2 w-56 bg-white border rounded-lg shadow-lg z-50">
                <div className="p-2">
                  <p className="text-xs text-slate-500 px-2 pb-2 font-medium">Seleziona formato:</p>
                  <button
                    onClick={() => handleExportCSV('woocommerce')}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100 rounded flex items-center gap-2"
                  >
                    <ShoppingBag className="w-4 h-4 text-purple-600" />
                    <div>
                      <p className="font-medium">WooCommerce</p>
                      <p className="text-xs text-slate-500">WordPress Shop</p>
                    </div>
                  </button>
                  <button
                    onClick={() => handleExportCSV('shopify')}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100 rounded flex items-center gap-2"
                  >
                    <ShoppingBag className="w-4 h-4 text-green-600" />
                    <div>
                      <p className="font-medium">Shopify</p>
                      <p className="text-xs text-slate-500">Shopify Store</p>
                    </div>
                  </button>
                  <button
                    onClick={() => handleExportCSV('generico')}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100 rounded flex items-center gap-2"
                  >
                    <Package className="w-4 h-4 text-slate-600" />
                    <div>
                      <p className="font-medium">CSV Generico</p>
                      <p className="text-xs text-slate-500">Tutti i campi</p>
                    </div>
                  </button>
                  <hr className="my-2" />
                  <label className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-100 rounded">
                    <input
                      type="checkbox"
                      checked={exportSoloRivendita}
                      onChange={(e) => setExportSoloRivendita(e.target.checked)}
                      className="rounded"
                    />
                    <span>Solo prodotti rivendita</span>
                  </label>
                </div>
              </div>
            )}
          </div>
          
          {/* Carico/Scarico compatto */}
          <Button
            onClick={() => setCaricoScaricoOpen(true)}
            size="sm"
            className="h-8 px-2 sm:px-3 text-xs sm:text-sm bg-emerald-600 hover:bg-emerald-700"
            data-testid="carico-scarico-btn"
          >
            <Truck className="w-3.5 h-3.5 sm:mr-1" />
            <span className="hidden sm:inline">Carico</span>
          </Button>
        </div>
      </div>

      {/* Statistiche compatte mobile */}
      <div className="grid grid-cols-4 gap-1.5 sm:gap-4">
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-2 sm:pt-4">
            <p className="text-purple-100 text-[9px] sm:text-sm truncate">Tecnici</p>
            <p className="text-base sm:text-3xl font-bold">{stats.tecnici}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
          <CardContent className="p-2 sm:pt-4">
            <p className="text-emerald-100 text-[9px] sm:text-sm truncate">Rivendita</p>
            <p className="text-base sm:text-3xl font-bold">{stats.rivendita}</p>
          </CardContent>
        </Card>
        
        <Card className={`${stats.scorteBasse > 0 ? 'bg-gradient-to-br from-red-500 to-red-600' : 'bg-gradient-to-br from-slate-400 to-slate-500'} text-white`}>
          <CardContent className="p-2 sm:pt-4">
            <p className="text-red-100 text-[9px] sm:text-sm truncate">Scorte</p>
            <p className="text-base sm:text-3xl font-bold">{stats.scorteBasse}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-2 sm:pt-4">
            <p className="text-blue-100 text-[9px] sm:text-sm truncate">Valore</p>
            <p className="text-base sm:text-2xl font-bold">€{stats.valoreTotale.toFixed(0)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Alert scorte basse */}
      {stats.scorteBasse > 0 && (
        <Card className="border-red-200 bg-red-50" data-testid="low-stock-alert">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <p className="text-red-700 font-medium">
              Attenzione: {stats.scorteBasse} prodott{stats.scorteBasse === 1 ? 'o' : 'i'} con scorte sotto la soglia minima
            </p>
          </CardContent>
        </Card>
      )}

      {/* Tabs Tecnico / Rivendita */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
          <TabsList className="grid w-full md:w-auto grid-cols-2 h-12">
            <TabsTrigger value="tecnico" className="gap-2 data-[state=active]:bg-purple-600 data-[state=active]:text-white">
              <Beaker className="w-4 h-4" />
              Prodotti Tecnici
            </TabsTrigger>
            <TabsTrigger value="rivendita" className="gap-2 data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
              <ShoppingBag className="w-4 h-4" />
              Rivendita
            </TabsTrigger>
          </TabsList>

          <Button 
            onClick={() => openNewDialog(activeTab)}
            className={`gap-2 ${activeTab === "tecnico" ? "bg-purple-600 hover:bg-purple-700" : "bg-emerald-600 hover:bg-emerald-700"}`}
            data-testid="add-prodotto-btn"
          >
            <Plus className="w-4 h-4" />
            Aggiungi {activeTab === "tecnico" ? "Prodotto Tecnico" : "Prodotto Rivendita"}
          </Button>
        </div>

        {/* Filtri */}
        <Card className="mb-4">
          <CardContent className="pt-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Cerca per nome, marca o codice..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="search-prodotti"
                />
              </div>
              
              {/* Pulsante Scanner Barcode */}
              <Button
                variant="outline"
                onClick={() => setScannerOpen(true)}
                className="gap-2 border-purple-200 text-purple-600 hover:bg-purple-50"
                data-testid="scanner-btn"
              >
                <Camera className="w-4 h-4" />
                <span className="hidden sm:inline">Scansiona</span>
              </Button>
              
              <Select value={filterCategoria} onValueChange={(v) => {
                setFilterCategoria(v);
                setFilterSottocategoria("all"); // Reset sottocategoria quando cambia categoria
              }}>
                <SelectTrigger className="w-full md:w-48" data-testid="filter-categoria">
                  <SelectValue placeholder="Tutte le categorie" />
                </SelectTrigger>
                <SelectContent position="popper" className="z-[9999]">
                  <SelectItem value="all">Tutte le categorie</SelectItem>
                  {categorieAttive.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>
                      <div className="flex items-center gap-2">
                        <cat.icon className="w-4 h-4" />
                        {cat.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Filtro Sottocategoria (visibile solo per categorie con sottocategorie) */}
              {filterCategoria !== "all" && getSottocategorie(filterCategoria).length > 0 && (
                <Select value={filterSottocategoria} onValueChange={setFilterSottocategoria}>
                  <SelectTrigger className="w-full md:w-48" data-testid="filter-sottocategoria">
                    <SelectValue placeholder="Tutte le sottocategorie" />
                  </SelectTrigger>
                  <SelectContent position="popper" className="z-[9999]">
                    <SelectItem value="all">Tutte le sottocategorie</SelectItem>
                    {getSottocategorie(filterCategoria).map(sub => (
                      <SelectItem key={sub.value} value={sub.value}>
                        {sub.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Filtri rapidi per categoria */}
            <div className="flex flex-wrap gap-2 mt-4">
              <Badge 
                variant={filterCategoria === "all" ? "default" : "outline"}
                className="cursor-pointer hover:bg-slate-100"
                onClick={() => {
                  setFilterCategoria("all");
                  setFilterSottocategoria("all");
                }}
              >
                Tutti
              </Badge>
              {categorieAttive.map(cat => (
                <Badge 
                  key={cat.value}
                  variant={filterCategoria === cat.value ? "default" : "outline"}
                  className={`cursor-pointer hover:opacity-80 ${filterCategoria === cat.value ? cat.color : ''}`}
                  onClick={() => {
                    setFilterCategoria(cat.value);
                    setFilterSottocategoria("all");
                  }}
                >
                  <cat.icon className="w-3 h-3 mr-1" />
                  {cat.label}
                </Badge>
              ))}
            </div>
            
            {/* Filtri rapidi per sottocategoria (quando categoria Colore è selezionata) */}
            {filterCategoria === "colore" && (
              <div className="flex flex-wrap gap-2 mt-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                <span className="text-sm font-medium text-purple-700 mr-2">Tipo Colore:</span>
                <Badge 
                  variant={filterSottocategoria === "all" ? "default" : "outline"}
                  className="cursor-pointer bg-purple-600 hover:bg-purple-700"
                  onClick={() => setFilterSottocategoria("all")}
                >
                  Tutti
                </Badge>
                {SOTTOCATEGORIE_COLORE.map(sub => (
                  <Badge 
                    key={sub.value}
                    variant={filterSottocategoria === sub.value ? "default" : "outline"}
                    className={`cursor-pointer ${filterSottocategoria === sub.value ? 'bg-purple-600' : 'hover:bg-purple-100 border-purple-300'}`}
                    onClick={() => setFilterSottocategoria(sub.value)}
                  >
                    {sub.label}
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Lista prodotti */}
        <TabsContent value="tecnico" className="mt-0">
          <ProductTable 
            prodotti={filteredProdotti}
            onEdit={openEditDialog}
            onDuplicate={openDuplicateDialog}
            onDelete={handleDelete}
            onQuantitaChange={handleQuantitaChange}
            getCategoriaInfo={getCategoriaInfo}
            emptyMessage="Nessun prodotto tecnico trovato"
            tipo="tecnico"
          />
        </TabsContent>

        <TabsContent value="rivendita" className="mt-0">
          <ProductTable 
            prodotti={filteredProdotti}
            onEdit={openEditDialog}
            onDuplicate={openDuplicateDialog}
            onDelete={handleDelete}
            onQuantitaChange={handleQuantitaChange}
            getCategoriaInfo={getCategoriaInfo}
            emptyMessage="Nessun prodotto rivendita trovato"
            tipo="rivendita"
          />
        </TabsContent>
      </Tabs>

      {/* Dialog Nuovo/Modifica Prodotto */}
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              {formData.tipo_prodotto === "tecnico" ? (
                <Beaker className="w-5 h-5 text-purple-600" />
              ) : (
                <ShoppingBag className="w-5 h-5 text-emerald-600" />
              )}
              {editingProdotto ? "Modifica Prodotto" : `Nuovo Prodotto ${formData.tipo_prodotto === "tecnico" ? "Tecnico" : "Rivendita"}`}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Nome e Marca/Fornitore */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nome Prodotto *</Label>
                <Input
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  placeholder="Es: Majirel 7.0"
                  required
                  data-testid="input-nome"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <Truck className="w-3 h-3" />
                  Marca / Fornitore *
                </Label>
                <Select 
                  value={formData.marca} 
                  onValueChange={(v) => setFormData({ ...formData, marca: v })}
                >
                  <SelectTrigger data-testid="select-marca">
                    <SelectValue placeholder="Seleziona fornitore..." />
                  </SelectTrigger>
                  <SelectContent 
                    position="popper" 
                    side="top"
                    className="max-h-[200px] overflow-y-auto z-[9999]"
                  >
                    {fornitori.map(f => (
                      <SelectItem key={f.id} value={f.nome}>
                        <div className="flex items-center gap-2">
                          <Truck className="w-3 h-3 text-slate-400" />
                          <span className="text-base">{f.nome}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Categoria e Sottocategoria */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Categoria *</Label>
                <Select 
                  value={formData.categoria} 
                  onValueChange={(v) => setFormData({ 
                    ...formData, 
                    categoria: v, 
                    sottocategoria: "",
                    unita_misura: v === "monouso" ? "pz" : formData.unita_misura
                  })}
                >
                  <SelectTrigger data-testid="select-categoria">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent position="popper" side="top" className="max-h-[200px] overflow-y-auto z-[9999]">
                    {(formData.tipo_prodotto === "tecnico" ? CATEGORIE_TECNICHE : CATEGORIE_RIVENDITA).map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>
                        <div className="flex items-center gap-2">
                          <cat.icon className="w-4 h-4" />
                          <span className="text-base">{cat.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {getSottocategorie(formData.categoria).length > 0 && (
                <div className="space-y-2">
                  <Label>Sottocategoria</Label>
                  <Select 
                    value={formData.sottocategoria} 
                    onValueChange={(v) => setFormData({ ...formData, sottocategoria: v })}
                  >
                    <SelectTrigger data-testid="select-sottocategoria">
                      <SelectValue placeholder="Seleziona..." />
                    </SelectTrigger>
                    <SelectContent position="popper" side="top" className="max-h-[200px] overflow-y-auto z-[9999]">
                      {getSottocategorie(formData.categoria).map(sub => (
                        <SelectItem key={sub.value} value={sub.value}>
                          <span className="text-base">{sub.label}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Sezione Toni e Riflessi per linee colore */}
            {formData.categoria === "colore" && formData.sottocategoria && (
              <div className="space-y-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200">
                <div className="flex items-center gap-2 text-amber-800">
                  <Palette className="w-5 h-5" />
                  <h4 className="font-semibold">Configurazione Linea Colore</h4>
                </div>
                <p className="text-xs text-amber-700">
                  Seleziona i toni e riflessi disponibili per questa linea. Verranno usati automaticamente nel Color Bar.
                </p>
                
                {/* Toni Disponibili */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-amber-800">Toni Disponibili</Label>
                  <div className="flex flex-wrap gap-2">
                    {TONI_DEFAULT.map(tono => {
                      const isSelected = formData.toni_disponibili?.includes(tono.value);
                      return (
                        <button
                          key={tono.value}
                          type="button"
                          onClick={() => {
                            const current = formData.toni_disponibili || [];
                            const updated = isSelected 
                              ? current.filter(t => t !== tono.value)
                              : [...current, tono.value];
                            setFormData({ ...formData, toni_disponibili: updated });
                          }}
                          className={`flex items-center gap-1.5 px-2 py-1 rounded-lg border transition-all text-xs ${
                            isSelected 
                              ? 'bg-amber-500 text-white border-amber-600 shadow-sm' 
                              : 'bg-white text-slate-600 border-slate-200 hover:border-amber-300'
                          }`}
                        >
                          <span 
                            className="w-4 h-4 rounded-full border border-white/50 shadow-inner"
                            style={{ backgroundColor: tono.hex }}
                          />
                          <span>{tono.value}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex gap-2 mt-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setFormData({ ...formData, toni_disponibili: TONI_DEFAULT.map(t => t.value) })}
                      className="text-xs h-7"
                    >
                      Seleziona Tutti
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setFormData({ ...formData, toni_disponibili: [] })}
                      className="text-xs h-7"
                    >
                      Deseleziona
                    </Button>
                  </div>
                </div>

                {/* Riflessi Disponibili */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-amber-800">Riflessi Disponibili</Label>
                  <div className="flex flex-wrap gap-2">
                    {RIFLESSI_DEFAULT.map(riflesso => {
                      const isSelected = formData.riflessi_disponibili?.includes(riflesso.value);
                      return (
                        <button
                          key={riflesso.value}
                          type="button"
                          onClick={() => {
                            const current = formData.riflessi_disponibili || [];
                            const updated = isSelected 
                              ? current.filter(r => r !== riflesso.value)
                              : [...current, riflesso.value];
                            setFormData({ ...formData, riflessi_disponibili: updated });
                          }}
                          className={`flex items-center gap-1.5 px-2 py-1 rounded-lg border transition-all text-xs ${
                            isSelected 
                              ? 'bg-purple-500 text-white border-purple-600 shadow-sm' 
                              : 'bg-white text-slate-600 border-slate-200 hover:border-purple-300'
                          }`}
                        >
                          <span 
                            className="w-4 h-4 rounded-full border border-white/50 shadow-inner"
                            style={{ backgroundColor: riflesso.hex }}
                          />
                          <span>.{riflesso.value}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex gap-2 mt-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setFormData({ ...formData, riflessi_disponibili: RIFLESSI_DEFAULT.map(r => r.value) })}
                      className="text-xs h-7"
                    >
                      Seleziona Tutti
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setFormData({ ...formData, riflessi_disponibili: [] })}
                      className="text-xs h-7"
                    >
                      Deseleziona
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Codice Barcode e Volume */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <Tag className="w-3 h-3" />
                  Codice a Barre
                </Label>
                <div className="flex gap-2">
                  <Input
                    value={formData.codice_prodotto}
                    onChange={(e) => setFormData({ ...formData, codice_prodotto: e.target.value })}
                    placeholder="Scansiona o digita..."
                    data-testid="input-codice"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setScannerOpen(true)}
                    className="shrink-0 border-purple-200 text-purple-600 hover:bg-purple-50"
                  >
                    <Camera className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Quantità / Volume</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    value={formData.volume_ml}
                    onChange={(e) => setFormData({ ...formData, volume_ml: e.target.value })}
                    placeholder="Es: 50, 100"
                    data-testid="input-volume"
                    className="flex-1"
                  />
                  <Select 
                    value={formData.unita_misura || "ml"} 
                    onValueChange={(v) => setFormData({ ...formData, unita_misura: v })}
                  >
                    <SelectTrigger className="w-28">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {UNITA_MISURA.map(u => (
                        <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Quantità e Soglia */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Quantità Iniziale *</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.quantita}
                  onChange={(e) => setFormData({ ...formData, quantita: e.target.value })}
                  required
                  data-testid="input-quantita"
                />
              </div>
              <div className="space-y-2">
                <Label>Soglia Minima (alert)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.soglia_minima}
                  onChange={(e) => setFormData({ ...formData, soglia_minima: e.target.value })}
                  data-testid="input-soglia"
                />
              </div>
            </div>

            {/* Prezzi */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prezzo Acquisto (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.prezzo_acquisto}
                  onChange={(e) => setFormData({ ...formData, prezzo_acquisto: e.target.value })}
                  required
                  data-testid="input-prezzo-acquisto"
                />
              </div>
              <div className="space-y-2">
                <Label>Prezzo Vendita (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.prezzo_vendita}
                  onChange={(e) => setFormData({ ...formData, prezzo_vendita: e.target.value })}
                  required
                  data-testid="input-prezzo-vendita"
                />
              </div>
            </div>

            {/* Genere target - solo per prodotti rivendita */}
            {formData.tipo_prodotto === "rivendita" && (
              <div className="space-y-2">
                <Label>Genere Target (per analisi rivendita)</Label>
                <Select
                  value={formData.genere_target}
                  onValueChange={(v) => setFormData({ ...formData, genere_target: v })}
                >
                  <SelectTrigger data-testid="select-genere-target">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unisex">Unisex</SelectItem>
                    <SelectItem value="uomo">Uomo</SelectItem>
                    <SelectItem value="donna">Donna</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Determina in quale categoria (uomo/donna) finisce questa vendita nei report fiche per genere.
                </p>
              </div>
            )}

            {/* Note */}
            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea
                value={formData.note}
                onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                placeholder="Note aggiuntive sul prodotto..."
                rows={2}
                data-testid="input-note"
              />
            </div>

            {/* Submit */}
            <Button 
              type="submit" 
              className={`w-full ${formData.tipo_prodotto === "tecnico" ? "bg-purple-600 hover:bg-purple-700" : "bg-emerald-600 hover:bg-emerald-700"}`}
              data-testid="submit-prodotto"
            >
              {editingProdotto ? "Salva Modifiche" : "Aggiungi Prodotto"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Scanner Barcode */}
      <BarcodeScanner
        open={scannerOpen}
        onOpenChange={setScannerOpen}
        prodottiDisponibili={prodotti}
        onProductFound={(prodotto) => {
          // Apri il dialog di modifica per il prodotto trovato
          openEditDialog(prodotto);
          toast.success(`Prodotto trovato: ${prodotto.nome}`);
        }}
        onProductNotFound={(barcode) => {
          // Apri dialog per creare nuovo prodotto con il codice scansionato
          setFormData({
            ...formData,
            codice_prodotto: barcode,
            codice_barre: barcode,
            tipo_prodotto: activeTab
          });
          setDialogOpen(true);
          toast.info(`Codice ${barcode} non trovato. Crea un nuovo prodotto.`);
        }}
      />

      {/* Dialog Carico/Scarico */}
      <Dialog open={caricoScaricoOpen} onOpenChange={setCaricoScaricoOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              <Truck className="w-5 h-5 text-emerald-600" />
              Carico / Scarico Magazzino
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Toggle Carico/Scarico */}
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant={caricoScaricoMode === "carico" ? "default" : "outline"}
                className={caricoScaricoMode === "carico" ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                onClick={() => setCaricoScaricoMode("carico")}
              >
                <ArrowDownToLine className="w-4 h-4 mr-2" />
                CARICO
              </Button>
              <Button
                type="button"
                variant={caricoScaricoMode === "scarico" ? "default" : "outline"}
                className={caricoScaricoMode === "scarico" ? "bg-red-600 hover:bg-red-700" : ""}
                onClick={() => setCaricoScaricoMode("scarico")}
              >
                <ArrowUpFromLine className="w-4 h-4 mr-2" />
                SCARICO
              </Button>
            </div>

            {/* Scanner per trovare prodotto */}
            <div className="space-y-2">
              <Label>Scansiona o cerca prodotto</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setScannerCaricoOpen(true)}
                  className="gap-2 flex-1 border-purple-200 text-purple-600 hover:bg-purple-50"
                >
                  <Camera className="w-4 h-4" />
                  Scansiona Barcode
                </Button>
              </div>
            </div>

            {/* Prodotto selezionato */}
            {selectedProdotto ? (
              <div className="p-3 bg-slate-50 rounded-lg border-2 border-emerald-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{selectedProdotto.nome}</p>
                    <p className="text-sm text-slate-500">{selectedProdotto.marca}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-slate-700">
                      Qta: {selectedProdotto.quantita}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-3 bg-slate-100 rounded-lg text-center text-slate-500">
                <Camera className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Scansiona un prodotto per iniziare</p>
              </div>
            )}

            {/* Quantità movimento */}
            {selectedProdotto && (
              <div className="space-y-2">
                <Label>Quantità da {caricoScaricoMode === "carico" ? "caricare" : "scaricare"}</Label>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setQuantitaMovimento(Math.max(1, quantitaMovimento - 1))}
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <Input
                    type="number"
                    min="1"
                    value={quantitaMovimento}
                    onChange={(e) => setQuantitaMovimento(Math.max(1, parseInt(e.target.value) || 1))}
                    className="text-center text-xl font-bold"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setQuantitaMovimento(quantitaMovimento + 1)}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Pulsante conferma */}
            {selectedProdotto && (
              <Button
                onClick={handleCaricoScarico}
                className={`w-full ${caricoScaricoMode === "carico" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}`}
              >
                {caricoScaricoMode === "carico" ? (
                  <>
                    <ArrowDownToLine className="w-4 h-4 mr-2" />
                    Carica {quantitaMovimento} pz
                  </>
                ) : (
                  <>
                    <ArrowUpFromLine className="w-4 h-4 mr-2" />
                    Scarica {quantitaMovimento} pz
                  </>
                )}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Scanner per Carico/Scarico */}
      <BarcodeScanner
        open={scannerCaricoOpen}
        onOpenChange={setScannerCaricoOpen}
        prodottiDisponibili={prodotti}
        onProductFound={(prodotto) => {
          setSelectedProdotto(prodotto);
          setQuantitaMovimento(1);
          toast.success(`Trovato: ${prodotto.nome}`);
        }}
        onProductNotFound={(barcode) => {
          toast.error(`Prodotto con codice ${barcode} non trovato in magazzino`);
        }}
      />
    </div>
  );
}

// Componente tabella prodotti
function ProductTable({ prodotti, onEdit, onDuplicate, onDelete, onQuantitaChange, getCategoriaInfo, emptyMessage, tipo }) {
  if (prodotti.length === 0) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center">
            <Package className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500 mb-4">{emptyMessage}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-lg" data-testid="prodotti-table">
      <CardContent className="p-0">
        {/* === DESKTOP (lg+): tabella classica === */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b">
                <th className="text-left p-4 font-medium text-slate-600">Prodotto</th>
                <th className="text-left p-4 font-medium text-slate-600">Marca</th>
                <th className="text-left p-4 font-medium text-slate-600">Categoria</th>
                <th className="text-center p-4 font-medium text-slate-600">Quantità</th>
                <th className="text-right p-4 font-medium text-slate-600">Prezzo</th>
                <th className="text-center p-4 font-medium text-slate-600">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {prodotti.map((prodotto) => {
                const catInfo = getCategoriaInfo(prodotto.categoria);
                const IconComponent = catInfo.icon || Package;
                const isLowStock = prodotto.quantita <= prodotto.soglia_minima;
                
                return (
                  <tr key={prodotto.id} className="border-b hover:bg-slate-50 transition-colors">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${catInfo.color}`}>
                          <IconComponent className="w-5 h-5" />
                        </div>
                        <div>
                          <span className="font-medium text-slate-800 block">{prodotto.nome}</span>
                          {prodotto.volume_ml && (
                            <span className="text-xs text-slate-400">{prodotto.volume_ml}ml</span>
                          )}
                          {prodotto.codice_prodotto && (
                            <span className="text-xs text-slate-400 ml-2">#{prodotto.codice_prodotto}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-slate-500">{prodotto.marca}</td>
                    <td className="p-4">
                      <Badge className={catInfo.color}>
                        {catInfo.label}
                      </Badge>
                      {prodotto.sottocategoria && (
                        <span className="text-xs text-slate-400 block mt-1">{prodotto.sottocategoria}</span>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-center gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 rounded-full"
                          onClick={() => onQuantitaChange(prodotto.id, -1)}
                          data-testid={`qty-minus-${prodotto.id}`}
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <span className={`font-bold text-lg w-12 text-center ${isLowStock ? 'text-red-500' : 'text-emerald-600'}`}>
                          {prodotto.quantita}
                        </span>
                        <Button
                          variant="outline"
                          size="icon"
                          className={`h-8 w-8 rounded-full border-0 text-white ${tipo === "tecnico" ? "bg-purple-600 hover:bg-purple-700" : "bg-emerald-600 hover:bg-emerald-700"}`}
                          onClick={() => onQuantitaChange(prodotto.id, 1)}
                          data-testid={`qty-plus-${prodotto.id}`}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      {isLowStock && (
                        <span className="text-xs text-red-500 block text-center mt-1 animate-pulse">
                          SCORTA BASSA
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-right">
                      <span className="font-medium">€{prodotto.prezzo_vendita?.toFixed(2)}</span>
                      <span className="text-xs text-slate-400 block">
                        Acq: €{prodotto.prezzo_acquisto?.toFixed(2)}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onEdit(prodotto)}
                          data-testid={`edit-${prodotto.id}`}
                          title="Modifica"
                        >
                          <Edit className="w-4 h-4 text-slate-500" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onDuplicate(prodotto)}
                          data-testid={`duplicate-${prodotto.id}`}
                          title="Duplica con stesse caratteristiche"
                        >
                          <Copy className="w-4 h-4 text-blue-500" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onDelete(prodotto.id)}
                          data-testid={`delete-${prodotto.id}`}
                          title="Elimina"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* === MOBILE / TABLET (< lg): cards verticali (no scroll orizzontale) === */}
        <div className="lg:hidden divide-y divide-slate-200" data-testid="prodotti-cards-mobile">
          {prodotti.map((prodotto) => {
            const catInfo = getCategoriaInfo(prodotto.categoria);
            const IconComponent = catInfo.icon || Package;
            const isLowStock = prodotto.quantita <= prodotto.soglia_minima;
            return (
              <div key={prodotto.id} className="p-3 flex flex-col gap-2 hover:bg-slate-50 transition-colors">
                {/* Riga 1: icona + nome/codice + prezzo */}
                <div className="flex items-start gap-3">
                  <div className={`w-11 h-11 rounded-lg flex items-center justify-center shrink-0 ${catInfo.color}`}>
                    <IconComponent className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-800 truncate">{prodotto.nome}</p>
                    <div className="flex items-center gap-2 text-xs text-slate-400 mt-0.5 flex-wrap">
                      {prodotto.marca && <span className="font-medium text-slate-600">{prodotto.marca}</span>}
                      {prodotto.volume_ml && <span>{prodotto.volume_ml}ml</span>}
                      {prodotto.codice_prodotto && <span>#{prodotto.codice_prodotto}</span>}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge className={`${catInfo.color} text-[10px]`}>{catInfo.label}</Badge>
                      {prodotto.sottocategoria && (
                        <span className="text-[10px] text-slate-400">{prodotto.sottocategoria}</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-bold text-slate-800">€{prodotto.prezzo_vendita?.toFixed(2)}</p>
                    <p className="text-[10px] text-slate-400">Acq: €{prodotto.prezzo_acquisto?.toFixed(2)}</p>
                  </div>
                </div>

                {/* Riga 2: quantità + azioni */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-9 w-9 rounded-full"
                      onClick={() => onQuantitaChange(prodotto.id, -1)}
                      data-testid={`qty-minus-m-${prodotto.id}`}
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <span className={`font-bold text-lg w-10 text-center ${isLowStock ? 'text-red-500' : 'text-emerald-600'}`}>
                      {prodotto.quantita}
                    </span>
                    <Button
                      variant="outline"
                      size="icon"
                      className={`h-9 w-9 rounded-full border-0 text-white ${tipo === "tecnico" ? "bg-purple-600 hover:bg-purple-700" : "bg-emerald-600 hover:bg-emerald-700"}`}
                      onClick={() => onQuantitaChange(prodotto.id, 1)}
                      data-testid={`qty-plus-m-${prodotto.id}`}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                    {isLowStock && (
                      <span className="text-[10px] text-red-500 font-bold ml-1 animate-pulse">SCORTA BASSA</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit(prodotto)}
                      data-testid={`edit-m-${prodotto.id}`}
                      title="Modifica"
                    >
                      <Edit className="w-4 h-4 text-slate-500" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDuplicate(prodotto)}
                      data-testid={`duplicate-m-${prodotto.id}`}
                      title="Duplica"
                    >
                      <Copy className="w-4 h-4 text-blue-500" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDelete(prodotto.id)}
                      data-testid={`delete-m-${prodotto.id}`}
                      title="Elimina"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
